/**
 * Green Flag — UpcomingRegistrationWidget
 *
 * Top-of-Events-page widget for the next event the viewer is registered for,
 * from registration until the event ends. Three shapes:
 *   - Getting ready (more than EVENT_SOON_HOURS out, people left to Spark):
 *     the event, then "Spark before you go" with Start Sparking as the main
 *     action.
 *   - Getting there (inside EVENT_SOON_HOURS): a map of the venue on top that
 *     opens the event page (check-in code, parking, directions), with Start
 *     Sparking below while there are still people to see.
 *   - Nothing to Spark (everyone seen, or a Friends Only viewer): the event's
 *     cover image with a "You're going" label; the whole card opens the event.
 *
 * It never says how many people are going.
 */

import { Box, Flex, Image, Text, chakra, useSlotRecipe } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronRight,
  faClock,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState } from "react";
import { PreSparkPrompt } from "../PreSparkPrompt/PreSparkPrompt";
import type { PreSparkState } from "../../lib/usePreSparkState";
import { formatEventWhen } from "../../lib/formatEventWhen";


export interface UpcomingRegistrationWidgetProps {
  title: string;
  startsAt: string;
  /** Venue name. */
  location?: string | null;
  img?: string | null;
  /** Google Maps embed for the venue; the map only shows when this is set. */
  mapEmbedUrl?: string;
  /** Inside the last EVENT_SOON_HOURS before the event. */
  soon: boolean;
  /**
   * Whether there is anyone left to Spark. "done" covers both "seen everyone"
   * and viewers who have no pre-event deck (Friends Only).
   */
  preSpark: PreSparkState;
  onOpenDetails: () => void;
  onStartSparking: () => void;
}

/** "Starts in 2 hr 15 min", or "Happening now" once it has begun. */
function startsInLabel(startsAt: string, now: number) {
  const mins = Math.ceil((new Date(startsAt).getTime() - now) / 60000);
  if (mins <= 0) return "Happening now";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `Starts in ${m} min`;
  return m ? `Starts in ${h} hr ${m} min` : `Starts in ${h} hr`;
}

/** Re-renders every minute while `active`, for the countdown chip. */
function useMinuteClock(active: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

export function UpcomingRegistrationWidget({
  title,
  startsAt,
  location,
  img,
  mapEmbedUrl,
  soon,
  preSpark,
  onOpenDetails,
  onStartSparking,
}: UpcomingRegistrationWidgetProps) {
  const recipe = useSlotRecipe({ key: "upcomingEligibleWidget" });
  const styles = recipe({});
  const now = useMinuteClock(soon);
  const when = formatEventWhen(startsAt, { tonight: true });
  const showMap = soon && !!mapEmbedUrl;

  // Nothing to Spark and not yet time to head over: just the event.
  if (preSpark === "done" && !showMap) {
    return (
      <chakra.button
        type="button"
        css={styles.root}
        w="full"
        border="none"
        p="0"
        textAlign="left"
        onClick={onOpenDetails}
        aria-label={`${title}, ${when}. You're going. Open event details.`}
        data-testid="upcoming-registration-widget"
      >
        <Box position="relative">
          {img ? (
            <Image css={styles.image} src={img} alt="" />
          ) : (
            <Box h="10rem" bg="border.default" />
          )}
          <Flex
            position="absolute"
            top="3"
            left="3"
            align="center"
            gap="1.5"
            bg="bg.canvas"
            color="fg.onTint"
            borderRadius="full"
            px="3"
            py="1.5"
            boxShadow="card"
          >
            <Flex
              boxSize="1rem"
              borderRadius="full"
              bg="bg.brand"
              color="fg.onBrand"
              align="center"
              justify="center"
              fontSize="0.5rem"
            >
              <FontAwesomeIcon icon={faCheck} />
            </Flex>
            <Text textStyle="caption">You're going</Text>
          </Flex>
        </Box>
        <Flex css={styles.body} direction="row" align="center" gap="3">
          <EventHeading when={when} title={title} location={location} />
          <Box color="fg.muted" flexShrink={0}>
            <FontAwesomeIcon icon={faChevronRight} />
          </Box>
        </Flex>
      </chakra.button>
    );
  }

  return (
    <Box
      css={styles.root}
      cursor="default"
      _hover={{}}
      _active={{}}
      data-testid="upcoming-registration-widget"
    >
      {showMap && (
        <chakra.button
          type="button"
          onClick={onOpenDetails}
          aria-label="Open event details: check-in code, parking and directions"
          position="relative"
          display="block"
          w="full"
          h="11rem"
          p="0"
          border="none"
          bg="border.default"
          cursor="pointer"
          overflow="hidden"
        >
          <chakra.iframe
            src={mapEmbedUrl}
            title=""
            aria-hidden="true"
            tabIndex={-1}
            loading="lazy"
            w="full"
            h="full"
            border="0"
            pointerEvents="none"
          />
          <Flex
            position="absolute"
            top="3"
            left="3"
            align="center"
            gap="1.5"
            bg="greenDark.solid"
            color="greenDark.contrast"
            borderRadius="full"
            px="3"
            py="1.5"
          >
            <FontAwesomeIcon icon={faClock} />
            <Text textStyle="caption">{startsInLabel(startsAt, now)}</Text>
          </Flex>
          <Flex
            position="absolute"
            right="3"
            bottom="3"
            align="center"
            gap="1"
            bg="bg.canvas"
            color="fg.default"
            borderRadius="full"
            px="3"
            py="2"
            boxShadow="card"
          >
            <Text textStyle="caption">Directions &amp; Check-In</Text>
            <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </Flex>
        </chakra.button>
      )}
      <Box css={styles.body} gap="4">
        <Flex align="flex-start" justify="space-between" gap="2">
          <EventHeading when={when} title={title} location={location} />
          {!showMap && (
            <Flex
              align="center"
              gap="1"
              bg="bg.brandSoft"
              color="fg.onTint"
              borderRadius="full"
              px="3"
              py="1"
              flexShrink={0}
            >
              <FontAwesomeIcon icon={faCheck} size="xs" />
              <Text textStyle="caption">Registered</Text>
            </Flex>
          )}
        </Flex>

        {preSpark === "available" && (
          <>
            <PreSparkPrompt
              bg="greenLight.subtle"
              borderRadius="sm"
              p="4"
            />
            <chakra.button
              type="button"
              css={styles.signUp}
              mt="0"
              gap="2"
              onClick={onStartSparking}
            >
              Start Sparking
              <FontAwesomeIcon icon={faChevronRight} size="xs" />
            </chakra.button>
          </>
        )}

        {!showMap && (
          <chakra.button
            type="button"
            alignSelf="center"
            bg="transparent"
            border="none"
            cursor="pointer"
            textStyle="textLink"
            color="greenLight.fg"
            textDecoration="underline"
            px="2"
            py="1"
            onClick={onOpenDetails}
          >
            View Event Details
          </chakra.button>
        )}
      </Box>
    </Box>
  );
}

function EventHeading({
  when,
  title,
  location,
}: {
  when: string;
  title: string;
  location?: string | null;
}) {
  const titleCss = useSlotRecipe({ key: "upcomingEligibleWidget" })({}).title;
  return (
    <Flex direction="column" gap="1.5" flex="1" minW="0">
      <Text textStyle="eyebrow" color="fg.muted">
        {when}
      </Text>
      <chakra.div css={titleCss}>{title}</chakra.div>
      {location && (
        <Flex align="center" gap="1.5" color="fg.muted">
          <FontAwesomeIcon icon={faLocationDot} size="xs" />
          <Text textStyle="bodySecondary">{location}</Text>
        </Flex>
      )}
    </Flex>
  );
}
